import _ from 'lodash';
import { defineComponent } from 'vue';
import BigNumber from 'bignumber.js';
import useConfigInject from '../../_util/hooks/useConfigInject';
import ScrollBar from '../../scrollbar';
import classNames from '../../_util/classNames';
import { inThanMinOrMax, numberFormat } from 'wetrade-tools';
import { getColors } from '../../_util/chartsColors';
export const LegendProps = () => ({
    // 画布高度
    height: {
        type: [Number, String],
        default: '300px',
    },
    // 图例数据
    legendList: {
        type: Array,
        default: () => [],
    },
    // 保留小数位n位
    digits: {
        type: Number,
        default: 2,
    },
    // 图例是否可以点击
    selectedMode: {
        type: Boolean,
        default: false,
    },
    // 图例选中状态表，动态配置图例显示项，selectedMode=true时生效
    selectedModel: {
        type: Object,
        default: () => {
            return {};
        },
    },
    // 是否需要截断，不要的字符放在[]里；例：有的银行名称一致，需要加（卡号）区分，显示时需要截断
    isLabelRemoveId: {
        type: Boolean,
        default: false,
    },
    // 颜色队列
    colors: {
        type: Array,
        default: () => [],
    },
    legendWidth: {
        type: String,
        default: '',
    },
    // 图例的值是数值型还是百分比
    isLegendRatio: {
        type: Boolean,
        default: false,
    },
    // 是否显示千位符
    showThousandSign: {
        type: Boolean,
        default: false,
    },
    // 数据总值
    totalValue: {
        type: Number,
        default: null,
    },
    // 数值是否需要换算，单位有万，亿，万亿，默认不换算
    isConversion: {
        type: [Boolean, Array],
        default: false,
    },
    unit: {
        type: [String, Array],
        default: '',
    },
    // 0和null显示为0还是--
    valueIsNumber: {
        type: Boolean,
        default: false,
    },
    legendMarginLeft: {
        type: String,
        default: '',
    },
    onLegendChange: {
        type: Function,
    },
    isValueComputed: {
        type: Boolean,
        default: false,
    },
});
const WdPieChartLegend = defineComponent({
    name: 'WdPieChartLegend',
    props: LegendProps(),
    setup(props, { emit, slots }) {
        const { prefixCls, configProvider } = useConfigInject('pie-chart-legend', props);
        return () => {
            const list = (props.legendList || []);
            const legendClass = classNames({
                [prefixCls.value]: true,
            });
            // 点击图例
            const handleClick = (t, index) => {
                emit('legendChange', t, index);
            };
            // 截取图例多余文字
            const showName = (value) => {
                const findIndexs = value?.indexOf('[');
                if (findIndexs > -1) {
                    const seriesName = value.substring(0, findIndexs);
                    return seriesName;
                }
                return value;
            };
            // 数值
            const valueFormatter = (item, index) => {
                // 显示比例
                if (props.isLegendRatio) {
                    if (![null, undefined].includes(item.percent))
                        return item.percent;
                    if (props.totalValue) {
                        // 如果值已计算好，直接显示
                        // 如果值未计算，用本项/总值 = 占比
                        const ratio = props.isValueComputed
                            ? item.value
                            : new BigNumber(item.value).div(props.totalValue).times(100).toNumber();
                        return inThanMinOrMax({
                            value: ratio || 0,
                            decimal: props.digits,
                            isPercent: true,
                        });
                    }
                    return props.valueIsNumber ? '0.00%' : '--';
                }
                // 显示数值，及是否要转换
                const isConversion = typeof props.isConversion === 'boolean' ? props.isConversion : props.isConversion[index];
                const unit = typeof props.unit === 'string' ? props.unit : props.unit[index];
                return item.value < 0.01 && item.value > 0
                    ? '<0.01'
                    : numberFormat(item.value, {
                        decimal: props.digits,
                        t: props.showThousandSign,
                        os: isConversion,
                    }) + (!_.isNil(item.value) ? unit : '');
            };
            const boxStyle = {
                marginLeft: props.legendMarginLeft,
                maxWidth: props.legendWidth,
            };
            return (<div class={legendClass} style={boxStyle}>
          <ScrollBar maxHeight={props.height} class={`${prefixCls.value}__scroll`}>
            {list.map((item, index) => {
                    const listClass = classNames({
                        [prefixCls.value + '__list']: true,
                        [prefixCls.value + '__list-no-select']: props.selectedMode && !props.selectedModel[item.name],
                    });
                    const colors = getColors(configProvider?.mode);
                    const nColor = props.colors.length > 0 ? props.colors : colors.COMMONCOLOR;
                    const symbolStyle = {
                        backgroundColor: nColor[index % nColor.length] || '',
                    };
                    return (<div class={listClass} onClick={() => handleClick(item, index)}>
                  <span style={symbolStyle} class={`${prefixCls.value}__list-dot`}></span>
                  <span class={`${prefixCls.value}__list-label`}>
                    {props.isLabelRemoveId
                            ? showName(item?.name || item)
                            : item?.name || (typeof item === 'string' ? item : '')}
                  </span>
                  {slots.mark?.(item)}
                  <span class={`${prefixCls.value}__list-value`}>
                    {valueFormatter(item, index)}
                  </span>
                </div>);
                })}
          </ScrollBar>
        </div>);
        };
    },
});
export default WdPieChartLegend;
